/**
 * Common utility functions for the Healthkon PulmoCare application
 */

/**
 * Get status color classes based on patient status
 */
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'On Track':
      return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
    case 'Missed':
      return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
    case 'Escalated':
      return 'bg-destructive/10 text-destructive border-destructive';
    default:
      return 'bg-muted text-muted-foreground border-muted';
  }
};

/**
 * Get adherence color based on percentage
 */
export const getAdherenceColor = (adherence: number): string => {
  if (adherence >= 85) return 'text-[#14CAF4]';
  if (adherence >= 70) return 'text-[#F07D13]';
  return 'text-destructive';
};

/**
 * Get adherence level label
 */
export const getAdherenceLevel = (adherence: number): 'high' | 'medium' | 'low' => {
  if (adherence >= 85) return 'high';
  if (adherence >= 70) return 'medium';
  return 'low';
};

/**
 * Get change color (for trends)
 */
export const getChangeColor = (change: number): string => {
  if (change > 0) return 'text-[#14CAF4]';
  if (change < 0) return 'text-destructive';
  return 'text-muted-foreground';
};

/**
 * Get change icon/indicator
 */
export const getChangeIcon = (change: number): string => {
  if (change > 0) return '↑';
  if (change < 0) return '↓';
  return '−';
};

/**
 * Get initials from full name
 */
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Format date to readable string
 */
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (d.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
};

/**
 * Format time to readable string
 */
export const formatTime = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  return formatDate(d);
};

/**
 * Get severity color for symptoms
 */
export const getSeverityColor = (severity: string): string => {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-destructive text-destructive-foreground';
    case 'severe':
      return 'bg-destructive/80 text-destructive-foreground';
    case 'moderate':
      return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
    case 'mild':
      return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

/**
 * Check if a value is within normal range
 */
export const isInNormalRange = (
  value: number,
  normalRange: { min: number; max: number }
): 'normal' | 'warning' | 'critical' => {
  if (value >= normalRange.min && value <= normalRange.max) return 'normal';
  if (value < normalRange.min * 0.9 || value > normalRange.max * 1.1) return 'critical';
  return 'warning';
};

/**
 * Normal ranges for common vitals
 */
export const VITAL_RANGES = {
  spo2: { min: 95, max: 100 },
  heartRate: { min: 60, max: 100 },
  systolicBP: { min: 90, max: 120 },
  diastolicBP: { min: 60, max: 80 },
  temperature: { min: 97.8, max: 99.1 },
  respiratoryRate: { min: 12, max: 20 },
  fev1: { min: 80, max: 120 },
  sixMWD: { min: 400, max: 700 }
};

/**
 * Calculate adherence percentage
 */
export const calculateAdherence = (
  completedTasks: number,
  totalTasks: number
): number => {
  if (totalTasks === 0) return 0;
  return Math.round((completedTasks / totalTasks) * 100);
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
};

/**
 * Filter array by search query (case-insensitive)
 */
export function filterBySearch<T>(
  items: T[],
  searchQuery: string,
  searchFields: (keyof T)[]
): T[] {
  if (!searchQuery.trim()) return items;

  const query = searchQuery.toLowerCase();
  return items.filter((item) =>
    searchFields.some((field) => {
      const value = item[field];
      return String(value).toLowerCase().includes(query);
    })
  );
}

/**
 * Sort array by field
 */
export function sortByField<T>(
  items: T[],
  field: keyof T,
  direction: 'asc' | 'desc' = 'asc'
): T[] {
  return [...items].sort((a, b) => {
    const aVal = a[field];
    const bVal = b[field];

    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

/**
 * Group items by field value
 */
export function groupBy<T>(
  items: T[],
  field: keyof T
): Record<string, T[]> {
  return items.reduce((groups, item) => {
    const key = String(item[field]);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {} as Record<string, T[]>);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Generate random ID
 */
export const generateId = (prefix: string = 'id'): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validate email
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^\+?[\d\s\-()]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
};

/**
 * Format phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

/**
 * Calculate age from birthdate
 */
export const calculateAge = (birthdate: Date | string): number => {
  const birth = typeof birthdate === 'string' ? new Date(birthdate) : birthdate;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: string): string => {
  const roleMap: Record<string, string> = {
    patient: 'Patient',
    doctor: 'Pulmonologist',
    therapist: 'Physiotherapist',
    coordinator: 'Care Coordinator',
    nurse: 'Home Nurse'
  };
  return roleMap[role.toLowerCase()] || role;
};

/**
 * Get role icon color
 */
export const getRoleColor = (role: string): string => {
  const colorMap: Record<string, string> = {
    patient: 'text-primary bg-primary/10',
    doctor: 'text-[#2762AC] bg-[#2762AC]/10',
    therapist: 'text-[#14CAF4] bg-[#14CAF4]/10',
    coordinator: 'text-[#F07D13] bg-[#F07D13]/10',
    nurse: 'text-[#494949] bg-[#494949]/10'
  };
  return colorMap[role.toLowerCase()] || 'text-muted-foreground bg-muted';
};
